import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import AppLayout from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ConfidenceChart } from '@/components/valuation/ConfidenceChart';
import { FlagsDisplay } from '@/components/valuation/FlagsDisplay';
import { ProductMixChart } from '@/components/valuation/ProductMixChart';
import { AssumptionsForm } from '@/components/valuation/AssumptionsForm';
import { formatCurrency, formatNumber } from '@/lib/format';
import { ArrowLeft, Play, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const PortfolioDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState<any>(null);
  const [currentRun, setCurrentRun] = useState<any>(null);
  const [trancheData, setTrancheData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadPortfolio();
  }, [id]);

  const loadPortfolio = async () => {
    if (!id) return;

    const { data: portfolioData } = await supabase
      .from('portfolios' as any)
      .select('*')
      .eq('id', id)
      .single();

    if (portfolioData) {
      setPortfolio(portfolioData as any);

      // Load latest run
      const { data: runData } = await supabase
        .from('runs' as any)
        .select('*')
        .eq('portfolio_id', id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (runData) {
        setCurrentRun(runData as any);
        
        // Load tranche outputs
        const { data: trancheData } = await supabase
          .from('tranche_outputs' as any)
          .select('*')
          .eq('run_id', (runData as any).id)
          .order('month_index', { ascending: true });

        if (trancheData) {
          setTrancheData(trancheData as any[]);
        }
      }
    }
  };

  const runValuation = async (assumptions: any) => {
    if (!id) return;

    setLoading(true);
    try {
      // Create assumptions
      const { data: assumptionsData, error: assumptionsError } = await supabase
        .from('assumptions' as any)
        .insert({
          ...assumptions,
          org_id: portfolio.org_id
        })
        .select()
        .single();

      if (assumptionsError) throw assumptionsError;

      // Call valuation edge function
      const { data, error } = await supabase.functions.invoke('valuation-run', {
        body: {
          portfolio_id: id,
          assumptions_id: (assumptionsData as any).id
        }
      });

      if (error) throw error;

      toast.success('Valuation completed successfully');
      loadPortfolio();
    } catch (error: any) {
      console.error('Valuation error:', error);
      toast.error(error.message || 'Failed to run valuation');
    } finally {
      setLoading(false);
    }
  };

  if (!portfolio) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <p className="text-muted-foreground">Loading portfolio...</p>
        </div>
      </AppLayout>
    );
  }

  const summary = currentRun?.summary || {};

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/portfolios')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold tracking-tight">{portfolio.name}</h1>
            <p className="text-muted-foreground">
              Portfolio valuation and analytics
            </p>
          </div>
          <Badge>{portfolio.status}</Badge>
        </div>

        {/* Key Metrics */}
        {currentRun && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>NPV (P50)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(summary.npv || 0)}</div>
                {summary.confidence && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {summary.confidence}% confidence
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Confidence Band</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-sm font-semibold">
                  {formatCurrency(summary.ci_low_npv || 0)} - {formatCurrency(summary.ci_high_npv || 0)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  P10 - P90 range
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription>IRR</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold flex items-center gap-2">
                  {summary.irr || '0.00'}%
                  <TrendingUp className="h-4 w-4 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Suggested Price</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(summary.suggested_price || 0)}
                </div>
                {summary.suggested_band && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Range: {formatCurrency(summary.suggested_band.min)} - {formatCurrency(summary.suggested_band.max)}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Flags */}
        {summary.flags && summary.flags.length > 0 && (
          <FlagsDisplay flags={summary.flags} />
        )}

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Charts */}
          <div className="space-y-6">
            {trancheData.length > 0 && (
              <ConfidenceChart data={trancheData} confidence={summary.confidence} />
            )}
            
            {summary.product_mix && (
              <ProductMixChart productMix={summary.product_mix} />
            )}
          </div>

          {/* Assumptions Form */}
          <div>
            <AssumptionsForm onSubmit={runValuation} loading={loading} />
          </div>
        </div>

        {/* Portfolio Info */}
        <Card>
          <CardHeader>
            <CardTitle>Portfolio Information</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="text-sm text-muted-foreground">Nominal Value</p>
              <p className="text-lg font-semibold">{formatCurrency(portfolio.nominal_value)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Account Count</p>
              <p className="text-lg font-semibold">{formatNumber(portfolio.account_count)}</p>
            </div>
            {summary.total_expected_collections && (
              <div>
                <p className="text-sm text-muted-foreground">Total Expected Collections</p>
                <p className="text-lg font-semibold">{formatCurrency(summary.total_expected_collections)}</p>
              </div>
            )}
            {summary.activation_costs && (
              <div>
                <p className="text-sm text-muted-foreground">Activation Costs</p>
                <p className="text-lg font-semibold">{formatCurrency(summary.activation_costs)}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default PortfolioDetail;
